import { Check, FileImage, Info, MapPin, Save, Settings, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import drawingService from '../../services/drawingService';
import {
  DrawingCategory,
  DrawingDiscipline,
  DrawingPhase,
  DrawingStatus,
  DrawingUploadData,
  ProjectDrawing,
} from '../../types';
import { Dropdown, Input, TextArea } from '../forms';

interface DrawingFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (drawing: ProjectDrawing) => void;
  projectId?: string;
  projects?: Array<{ value: string; label: string }>;
  editingDrawing?: ProjectDrawing | null;
  mode?: 'create' | 'edit';
}

const DrawingFormDrawer: React.FC<DrawingFormDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  projects = [],
  editingDrawing,
  mode = 'create',
}) => {
  const toast = useToast();
  const isEditMode = mode === 'edit' && editingDrawing;

  // State
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Configuration options
  const [categories, setCategories] = useState<
    Array<{ value: string; label: string; color: string }>
  >([]);
  const [statuses, setStatuses] = useState<Array<{ value: string; label: string; color: string }>>(
    []
  );
  const [disciplines, setDisciplines] = useState<Array<{ value: string; label: string }>>([]);
  const [phases, setPhases] = useState<Array<{ value: string; label: string }>>([]);
  const [sheetSizes, setSheetSizes] = useState<Array<{ value: string; label: string }>>([]);
  const [visibilityOptions, setVisibilityOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  // Form data
  const [formData, setFormData] = useState<DrawingUploadData>({
    project_id: projectId || '',
    name: '',
    description: '',
    category: 'floor_plan' as DrawingCategory,
    status: 'draft' as DrawingStatus,
    tags: [],
    location: {
      site: '',
      building: '',
      floor: '',
      room: '',
      section: '',
    },
    metadata: {
      version: '1.0',
      revision: '',
      scale: '',
      sheet_size: 'A3',
      drawing_number: '',
      discipline: 'architectural' as DrawingDiscipline,
      phase: 'design_development' as DrawingPhase,
    },
    visibility: 'team_only',
  });

  // Tag input
  const [tagInput, setTagInput] = useState('');

  // Load configuration options
  useEffect(() => {
    if (isOpen) {
      loadConfigurations();
      if (isEditMode) {
        populateFormFromDrawing();
      }
    }
  }, [isOpen, isEditMode, editingDrawing]);

  const populateFormFromDrawing = () => {
    if (!editingDrawing) return;

    setFormData({
      project_id: editingDrawing.project_id,
      name: editingDrawing.name,
      description: editingDrawing.description || '',
      category: editingDrawing.category,
      status: editingDrawing.status,
      tags: editingDrawing.tags || [],
      location: editingDrawing.location,
      metadata: {
        version: editingDrawing.metadata.version,
        revision: editingDrawing.metadata.revision || '',
        scale: editingDrawing.metadata.scale || '',
        sheet_size: editingDrawing.metadata.sheet_size,
        drawing_number: editingDrawing.metadata.drawing_number || '',
        discipline: editingDrawing.metadata.discipline,
        phase: editingDrawing.metadata.phase,
      },
      visibility: editingDrawing.visibility,
    });
  };

  const loadConfigurations = async () => {
    try {
      const configs = await drawingService.getDrawingConfigurations();
      setCategories(configs.categories);
      setStatuses(configs.statuses);
      setDisciplines(configs.disciplines);
      setPhases(configs.phases);
      setSheetSizes(configs.sheetSizes);
      setVisibilityOptions(configs.visibilityOptions);
    } catch (error) {
      console.error('Failed to load configurations:', error);
    }
  };

  // File handling
  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      const maxSize = 50 * 1024 * 1024; // 50MB

      if (selectedFile.size > maxSize) {
        toast.error('File size must be less than 50MB');
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/vnd.ms-visio',
        'image/vnd.dwg',
        'application/acad',
      ];

      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.vsd', '.dwg', '.dxf'];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf('.'));

      if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
        toast.error('Only drawing files (PDF, DWG, DXF, Images, Visio) are allowed');
        return;
      }

      setFile(selectedFile);

      // Auto-populate name if empty and not editing
      if (!formData.name && !isEditMode) {
        const nameWithoutExtension = selectedFile.name.substring(
          0,
          selectedFile.name.lastIndexOf('.')
        );
        setFormData(prev => ({ ...prev, name: nameWithoutExtension }));
      }
    },
    [formData.name, isEditMode, toast]
  );

  // Drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // Tag management
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditMode && !file) {
      toast.error('Please select a drawing file');
      return;
    }

    if (!formData.project_id || !formData.name || !formData.location.site) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let result;

      if (isEditMode && editingDrawing) {
        // Update existing drawing
        result = await drawingService.updateDrawing(
          editingDrawing._id,
          formData,
          file,
          progress => setUploadProgress(progress),
          projectId
        );
      } else {
        // Create new drawing
        result = await drawingService.uploadDrawing(
          file!,
          formData,
          progress => setUploadProgress(progress),
          projectId
        );
      }

      if (result.success && result.data) {
        toast.success(`Drawing ${isEditMode ? 'updated' : 'uploaded'} successfully`);
        onSuccess?.(result.data);
        handleClose();
      } else {
        toast.error(result.message || `Failed to ${isEditMode ? 'update' : 'upload'} drawing`);
      }
    } catch (error) {
      console.error(`${isEditMode ? 'Update' : 'Upload'} failed:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'upload'} drawing`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setFormData({
        project_id: projectId || '',
        name: '',
        description: '',
        category: 'floor_plan',
        status: 'draft',
        tags: [],
        location: {
          site: '',
          building: '',
          floor: '',
          room: '',
          section: '',
        },
        metadata: {
          version: '1.0',
          revision: '',
          scale: '',
          sheet_size: 'A3',
          drawing_number: '',
          discipline: 'architectural',
          phase: 'design_development',
        },
        visibility: 'team_only',
      });
      setTagInput('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={handleClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <FileImage className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Edit Drawing' : 'Upload Drawing'}
              </h3>
              <p className="text-sm text-gray-600">
                {isEditMode
                  ? 'Update drawing information and optionally replace the file'
                  : 'Add a new drawing to the project with metadata and technical specifications'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form id="drawing-form" onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Project Selection */}
            {!projectId && (
              <Dropdown
                label="Project *"
                placeholder="Select project"
                options={projects}
                value={formData.project_id}
                onChange={value => setFormData(prev => ({ ...prev, project_id: value }))}
                required
              />
            )}

            {/* File Upload (only show in create mode or optionally in edit mode) */}
            {(!isEditMode || !editingDrawing) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Drawing File *
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver
                      ? 'border-red-400 bg-red-50'
                      : file
                        ? 'border-green-400 bg-green-50'
                        : 'border-gray-300 hover:border-red-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.vsd"
                    onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                    id="drawing-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="drawing-upload" className="cursor-pointer">
                    {file ? (
                      <div className="space-y-2">
                        <Check className="h-8 w-8 text-green-500 mx-auto" />
                        <div className="text-sm font-medium text-green-700">{file.name}</div>
                        <div className="text-xs text-green-600">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <FileImage className="h-8 w-8 text-gray-400 mx-auto" />
                        <div className="text-sm">
                          <span className="font-medium text-red-500 hover:text-red-400">
                            Click to upload
                          </span>
                          <span className="text-gray-500"> or drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, DWG, DXF, Images, Visio (max 50MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Edit mode: show existing file info and optional replacement */}
            {isEditMode && editingDrawing && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Current File</h4>
                  <div className="flex items-center space-x-3">
                    <FileImage className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-700">
                        {editingDrawing.file_info.original_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(editingDrawing.file_info.file_size / (1024 * 1024)).toFixed(2)} MB •
                        {editingDrawing.file_info.file_extension.toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Replace File (Optional)
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      dragOver
                        ? 'border-red-400 bg-red-50'
                        : file
                          ? 'border-green-400 bg-green-50'
                          : 'border-gray-300 hover:border-red-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.vsd"
                      onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                      className="hidden"
                      id="drawing-replace"
                      disabled={uploading}
                    />
                    <label htmlFor="drawing-replace" className="cursor-pointer">
                      {file ? (
                        <div className="space-y-2">
                          <Check className="h-6 w-6 text-green-500 mx-auto" />
                          <div className="text-sm font-medium text-green-700">{file.name}</div>
                          <div className="text-xs text-green-600">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB - New file selected
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-6 w-6 text-gray-400 mx-auto" />
                          <div className="text-sm text-gray-600">
                            Click to select a new file or drag and drop
                          </div>
                          <p className="text-xs text-gray-500">Leave empty to keep current file</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <Info className="h-4 w-4 mr-2" />
                Basic Information
              </h3>

              <Input
                label="Drawing Name *"
                placeholder="Enter drawing name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />

              <TextArea
                label="Description"
                placeholder="Brief description of the drawing"
                value={formData.description || ''}
                onChange={value => setFormData(prev => ({ ...prev, description: value }))}
                rows={3}
              />

              <div className="grid grid-cols-2 gap-4">
                <Dropdown
                  label="Category *"
                  options={categories}
                  value={formData.category}
                  onChange={value =>
                    setFormData(prev => ({ ...prev, category: value as DrawingCategory }))
                  }
                  required
                />

                <Dropdown
                  label="Status *"
                  options={statuses}
                  value={formData.status}
                  onChange={value =>
                    setFormData(prev => ({ ...prev, status: value as DrawingStatus }))
                  }
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="bg-brand-50 p-4 rounded-lg space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Location Information
              </h3>

              <Input
                label="Site *"
                placeholder="Site name or identifier"
                value={formData.location.site}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    location: { ...prev.location, site: e.target.value },
                  }))
                }
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Building"
                  placeholder="Building name/number"
                  value={formData.location.building || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      location: { ...prev.location, building: e.target.value },
                    }))
                  }
                />

                <Input
                  label="Floor"
                  placeholder="Floor level"
                  value={formData.location.floor || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      location: { ...prev.location, floor: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Room"
                  placeholder="Room number/name"
                  value={formData.location.room || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      location: { ...prev.location, room: e.target.value },
                    }))
                  }
                />

                <Input
                  label="Section"
                  placeholder="Section/Zone"
                  value={formData.location.section || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      location: { ...prev.location, section: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            {/* Technical Details */}
            <div className="bg-green-50 p-4 rounded-lg space-y-4">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                Technical Specifications
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Version"
                  placeholder="1.0"
                  value={formData.metadata.version}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, version: e.target.value },
                    }))
                  }
                />

                <Input
                  label="Revision"
                  placeholder="A, B, C..."
                  value={formData.metadata.revision || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, revision: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Drawing Number"
                  placeholder="A-001, E-101..."
                  value={formData.metadata.drawing_number || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, drawing_number: e.target.value },
                    }))
                  }
                />

                <Input
                  label="Scale"
                  placeholder='1:100, 1/4"=1&apos;-0"...'
                  value={formData.metadata.scale || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, scale: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Dropdown
                  label="Discipline"
                  options={disciplines}
                  value={formData.metadata.discipline}
                  onChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, discipline: value as DrawingDiscipline },
                    }))
                  }
                />

                <Dropdown
                  label="Phase"
                  options={phases}
                  value={formData.metadata.phase}
                  onChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, phase: value as DrawingPhase },
                    }))
                  }
                />

                <Dropdown
                  label="Sheet Size"
                  options={sheetSizes}
                  value={formData.metadata.sheet_size}
                  onChange={value =>
                    setFormData(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, sheet_size: value as any },
                    }))
                  }
                />
              </div>
            </div>

            {/* Tags & Visibility */}
            <div className="bg-purple-50 p-4 rounded-lg space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Tags & Visibility</h3>

              <div className="space-y-4">
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add tag..."
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyPress={handleTagInputKeyPress}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Add
                  </button>
                </div>

                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <Dropdown
                  label="Visibility"
                  options={visibilityOptions}
                  value={formData.visibility}
                  onChange={value => setFormData(prev => ({ ...prev, visibility: value as any }))}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-white rounded-b-xl">
          {uploading && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>{isEditMode ? 'Updating...' : 'Uploading...'}</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="drawing-form"
              disabled={
                uploading || (!isEditMode && !file) || !formData.name || !formData.location.site
              }
              className="px-6 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {isEditMode ? 'Updating...' : 'Uploading...'}
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <Save className="h-4 w-4 mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {isEditMode ? 'Update Drawing' : 'Upload Drawing'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default DrawingFormDrawer;
